/**
 * Deposit Service
 * API functions for deposit operations using React Query
 */

import axios from 'axios';
import { API } from '@/utils/helper';
import { getUID } from '@/utils/helper';

/**
 * Fetch coin details (networks, coinId, etc.)
 * @param {string} symbol - Coin symbol (BTC, ETH, etc.)
 * @returns {Promise}
 */
export const fetchCoinDetails = async (symbol) => {
  const response = await axios.post(`${API}/getCoinDetails`, {
    symbol: symbol.toUpperCase()
  });
  return response.data;
};

/**
 * Create crypto deposit
 * @param {object} payload - Deposit payload
 * @returns {Promise}
 */
export const createCryptoDeposit = async (payload) => {
  const response = await axios.post(`${API}/createDeposit`, {
    ...payload,
    userid: getUID().toString()
  });
  return response.data;
};

/**
 * Fetch bank details for fiat currencies
 * @param {string} currency - Currency code (INR, MVR, etc.)
 * @returns {Promise}
 */
export const fetchBankDetails = async (currency) => {
  const response = await fetch(`${API}/bankDetails/${currency}`);
  if (!response.ok) {
    throw new Error('Failed to fetch bank details');
  }
  return response.json();
};

/**
 * Create INR automatic payment (UPI/PhonePe)
 * @param {object} payload - Payment payload
 * @returns {Promise}
 */
export const createINRPayment = async (payload) => {
  const response = await axios.post(`${API}/remotes/create-deposit`, {
    amount: payload.amount,
    currency: 'INR',
    custom_user_id: getUID(),
    custom_transaction_id: `TX_${Date.now()}`,
    payment_system: payload.paymentSystem || 'phonepe',
    webhook_id: 2472417,
    return_url: 'https://jackopot.com',
  });
  return response.data;
};

/**
 * Create INR payment via Ok Pay
 * @param {object} payload - Payment payload
 * @returns {Promise}
 */
export const createOkPayPayment = async (payload) => {
  const response = await axios.post(`${API}/api/payments/payin/initiate`, {
    userId: getUID(),
    currency: 'INR',
    out_trade_no: `TX${Date.now()}`,
    pay_type: 'UPI',
    money: payload.amount,
    attach: '',
    notify_url: `${API}/api/payments/payin/callback`,
    returnUrl: 'https://jackopot.com',
    phone: payload.phoneNumber
  });
  return response.data;
};

/**
 * Create manual fiat deposit (with transaction ID and screenshot)
 * @param {FormData} formData - Form data with transaction details
 * @returns {Promise}
 */
export const createManualDeposit = async (formData) => {
  const response = await axios.post(`${API}/api/deposits/create`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Create CricPay payment (UPI 3 or Net Banking)
 * @param {object} payload - Payment payload
 * @returns {Promise}
 */
export const createCricPayPayment = async (payload) => {
  const response = await axios.post(`${API}/cricpay/payment-request`, {
    amount: payload.amount,
    userId: getUID(),
    paymentMethod: payload.paymentMethod, // PS002 for UPI, PS003 for Net Banking
    userName: localStorage.getItem('name') || 'User'
  });
  return response.data;
};
